import { jwtDecode } from 'jwt-decode';
import { stripBearerToken } from './bearer-token.util';

export type IamJwtClaims = {
  roles?: string[];
  groupIds?: string[];
  context?: string;
  isImpersonated?: boolean;
};

export function decodeIamClaims(token?: string | null): IamJwtClaims {
  try {
    const raw = stripBearerToken(token);

    if (!raw) {
      return {};
    }

    const payload = jwtDecode<IamJwtClaims>(raw);

    return {
      roles: Array.isArray(payload.roles) ? payload.roles.map(String) : [],
      groupIds: Array.isArray(payload.groupIds) ? payload.groupIds.map(String) : [],
      context: payload.context,
      isImpersonated: payload.isImpersonated === true,
    };
  } catch {
    return {};
  }
}

export function mergeIamUser<T extends { roles?: string[]; groupIds?: string[]; context?: string }>(
  user: T | null,
  token?: string | null,
): T | null {
  if (!user) {
    return null;
  }

  const claims = decodeIamClaims(token);
  const roles = firstNonEmpty(user.roles, claims.roles);
  const groupIds = firstNonEmpty(user.groupIds, claims.groupIds);
  const context = user.context ?? claims.context;

  return {
    ...user,
    ...(roles ? { roles } : {}),
    ...(groupIds ? { groupIds } : {}),
    ...(context ? { context } : {}),
  };
}

export function isImpersonatedToken(token?: string | null): boolean {
  return decodeIamClaims(token).isImpersonated === true;
}

function firstNonEmpty(primary?: string[], fallback?: string[]): string[] | undefined {
  if (primary?.length) {
    return primary;
  }

  if (fallback?.length) {
    return fallback;
  }

  return undefined;
}

export function getTokenExpiration(token?: string | null): number | null {
  try {
    const raw = stripBearerToken(token);

    if (!raw) {
      return null;
    }

    const { exp } = jwtDecode<{ exp?: number }>(raw);

    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}
